/**
 * Contact Types
 */

export enum CategoryCode {
    Corporate = 'B09001',
    Individual = 'B09002'
}

export enum ContactRegisterType {
    Individual = 'Individual',
    Corp = 'Corp'
}

/** SPC Address Consolidation dialog tab keys (matches fetchSpcContactAddressBundle group names). */
export type SpcAddressConsolidationGroupTab = 'Residential' | 'Service' | 'Business / BO';

/** Contact address candidate source (table-derived, not user-selectable). */
export type ContactAddressCandidateSource = 'SPC' | 'SYNC' | 'LETTAGA';

/** M04 address type code for candidates. */
export type ContactAddressTypeCode = 'M04001' | 'M04002' | 'M04003';

export interface ContactAddressCandidate {
    candidateId: string;
    sourceSystem: ContactAddressCandidateSource;
    addressType: ContactAddressTypeCode;
    addressTypeLabel: string;
    address: Record<string, unknown>;
    displayLine: string;
    meta?: Record<string, unknown>;
}

export interface ContactAddressCandidatesResponse {
    contactCd: string;
    candidates: ContactAddressCandidate[];
    counts: {
        SPC: number;
        SYNC: number;
        LETTAGA: number;
        total: number;
    };
}

export interface AddressInfo {
    postalCode?: string;
    blockHouseNo?: string;
    streetName?: string;
    buildingEstateName?: string;
    level?: string;
    unit?: string;
    addressLine1?: string;
    addressLine2?: string;
    // MY인 경우 Entity 스타일 주소 필드
    addressLine3?: string;
    country?: string;
    state?: string;
    city?: string;
    postcode?: string;
    post_code?: string; // JSON에서 오는 경우 (snake_case)
    // MY인 경우 바인딩용 GUID/CODE
    countryCode?: string;
    stateCode?: string;
    cityCode?: string;
    postcodeCode?: string;
    /** MY Contact List — L_CONTACT_ADDRESS.ADDRESS_ID */
    address_id?: number | string;
    addressId?: number | string;
    ADDRESS_ID?: number | string;
}

export interface ContactInfo {
    countryCode: string;
    areaCode?: string;
    mobileNos: string[];
    emails: string[];
    emailMainYn?: boolean[]; // 각 이메일의 MAIN_YN 값 (체크박스 상태)
    emailSyncYn?: Array<boolean | number>; // 각 이메일의 SYNC_YN 값
    note?: string;
}

export interface CreateIndividualData {
    identificationType: string;
    identificationNo: string;
    nationality: string;
    dateOfBirth?: string;
    passportNoExpiryDate?: string;
    /** MY Registrable Individual: 단일 Name (CONTACT_NAME) */
    contactName?: string;
    firstName?: string;
    middleName?: string;
    surname?: string;
    profileGender?: string;
    profileRace?: string;
    profileOccupation?: string;
    profileSecretaryLicenseNo?: string;
    profileSecretaryCertificateNo?: string;
    /** MY JSON_DATA.OtherDirectorship */
    profileOtherDirectorship?: string;
    /** MY JSON_DATA.FirmNo */
    profileFirmNo?: string;
    /** MY JSON_DATA.NEW_REG_NO — Corporate 신규 등록번호 */
    profileNewRegNo?: string;
    /** MY JSON_DATA.issuing_country */
    profileIssuingCountry?: string;
    /** MY JSON_DATA.place_of_birth */
    profilePlaceOfBirth?: string;
    /** MY JSON_DATA.is_meeting (0 | 1) */
    profileIsMeeting?: number | null;
    addressType: 'B07001' | 'B07002'; // 'B07001' = Local Address, 'B07002' = Foreign Address
    localAddress?: AddressInfo;
    foreignAddress?: AddressInfo;
    contactInfo: ContactInfo;
}

/** POST /Contact/individual — CreateIndividualData + MY alternate / email flags */
export interface CreateIndividualContactInfo extends ContactInfo {
    emailMainYn?: boolean[];
    emailSyncYn?: number[];
    altLocalAddress?: AddressInfo & Record<string, unknown>;
    altForeignAddress?: Partial<AddressInfo>;
    serviceAddressList?: Record<string, unknown>[];
    businessAddressList?: Record<string, unknown>[];
}

export interface CreateIndividualPayload extends Omit<CreateIndividualData, 'contactInfo'> {
    contactInfo: CreateIndividualContactInfo;
}

export interface CreateCorporateData {
    jurisdiction: string;
    entityName: string;
    incorporationDate?: string;
    publicAccountingEntities: 'Yes' | 'No';
    registrationNumber: string;
    representativeName?: string;
    legalForm?: string;
    incorporationStatute?: string;
    corporateEntityRegister?: string;
    /** MY: JSON_DATA.FirmNo */
    profileFirmNo?: string;
    addressType: 'B07001' | 'B07002'; // 'B07001' = Local Address, 'B07002' = Foreign Address
    localAddress?: AddressInfo;
    foreignAddress?: AddressInfo;
    contactInfo: ContactInfo;
    /** MY: true 면 생성 시 L_CONTACT_BRANCH 저장을 건너뛴다 (Register ADD 는 주소를 register API 로 따로 저장) */
    skipMyBranchAddressSave?: boolean;
}

export interface Contact {
    // snake_case 필드 (백엔드에서 반환하는 형식)
    CONTACT_CD: string;
    CONTACT_REGISTER_TYPE?: string;
    CATEGORY_CD: string; // 'B09001' (Corporate) or 'B09002' (Individual)
    CONTACT_REG_NO?: string; // 암호화
    CONTACT_NAME?: string; // 암호화 (Corporate)
    FIRST_NAME?: string; // 암호화 (Individual)
    MIDDLE_NAME?: string; // 암호화 (Individual)
    SURNAME?: string; // 암호화 (Individual)
    DATE_OF_BIRTH?: string; // 암호화 (Individual)
    PASSPORT_NO_EXPIRY_DATE?: string; // 암호화 (Individual)
    ID_TYPE_CD?: string;
    NATIONALITY_CD?: string;
    ADDRESS_TYPE_CD?: string;
    POSTAL_CODE?: string;
    BLOCK_NO?: string;
    STREET_NAME?: string;
    BUILDING_NAME?: string;
    ADDR_LEVEL?: string;
    ADDR_UNIT?: string;
    FOREIGN_ADDRESS_LINE_1?: string;
    FOREIGN_ADDRESS_LINE_2?: string;
    ALT_ADDRESS_TYPE_CD?: string;
    ALT_POSTAL_CODE?: string;
    ALT_BLOCK_NO?: string;
    ALT_STREET_NAME?: string;
    ALT_BUILDING_NAME?: string;
    ALT_ADDR_LEVEL?: string;
    ALT_ADDR_UNIT?: string;
    ALT_FOREIGN_ADDRESS_LINE_1?: string;
    ALT_FOREIGN_ADDRESS_LINE_2?: string;
    CONTACT_EMAIL?: string; // 암호화
    COUNTRY_CD?: string;
    AREA_CODE?: string;
    MOBILE_NO?: string; // 암호화
    JURISDICTION?: string; // Corporate
    DATE_OF_INCORPORATION?: string; // Corporate
    REPRESENTATIVE_CD?: string; // Corporate
    LEGAL_FORM?: string; // Corporate
    INCORPORATION_STATUTE?: string; // Corporate
    CORPORATE_ENTITY_REGISTER?: string; // Corporate
    AUDITOR_YN?: boolean | string; // Corporate ('Yes' | 'No' 또는 boolean)
    NOTE?: string;
    PROFILE_GENDER?: string;
    PROFILE_RACE?: string;
    PROFILE_OCCUPATION?: string;
    PROFILE_SECRETARY_LICENSE_NO?: string;
    PROFILE_SECRETARY_CERTIFICATE_NO?: string;
    PROFILE_OTHER_DIRECTORSHIP?: string;
    PROFILE_FIRM_NO?: string;
    PROFILE_NEW_REG_NO?: string;
    PROFILE_ISSUING_COUNTRY?: string;
    PROFILE_PLACE_OF_BIRTH?: string;
    PROFILE_IS_MEETING?: number | null;
    IS_DEL?: boolean;
    SYSREGDATE?: string;
    SYSMODDATE?: string;
    /** MY Corporate: 주소가 L_CONTACT_BRANCH / MAP 에서 로드됨 */
    USE_BRANCH_ADDRESSES?: boolean;
    BRANCH_ENTITY_CD?: string | null;
    residential_address?: Record<string, unknown>[] | Record<string, unknown>;
    /** MY Corporate branch — L_CONTACT_BRANCH M04002 */
    service_address?: Record<string, unknown>[];
    /** MY Corporate branch — L_CONTACT_BRANCH M04003 */
    business_address?: Record<string, unknown>[];
    CONTACT_JSON_DATA?: string | Record<string, unknown>;
    
    // 하위 호환성을 위한 camelCase 필드 (deprecated)
    contactCd?: string;
    contactRegisterType?: string;
    categoryCd?: string;
    contactRegNo?: string;
    contactName?: string;
    firstName?: string;
    middleName?: string;
    surname?: string;
    dateOfBirth?: string;
    passportNoExpiryDate?: string;
    idTypeCd?: string;
    nationalityCd?: string;
    addressTypeCd?: string;
    postalCode?: string;
    blockNo?: string;
    streetName?: string;
    buildingName?: string;
    addrLevel?: string;
    addrUnit?: string;
    foreignAddressLine1?: string;
    foreignAddressLine2?: string;
    altAddressTypeCd?: string;
    altPostalCode?: string;
    altBlockNo?: string;
    altStreetName?: string;
    altBuildingName?: string;
    altAddrLevel?: string;
    altAddrUnit?: string;
    altForeignAddressLine1?: string;
    altForeignAddressLine2?: string;
    contactEmail?: string;
    countryCd?: string;
    areaCode?: string;
    mobileNo?: string;
    jurisdiction?: string;
    dateOfIncorporation?: string;
    representativeCd?: string;
    legalForm?: string;
    incorporationStatute?: string;
    corporateEntityRegister?: string;
    auditorYn?: boolean;
    note?: string;
    profileGender?: string;
    profileRace?: string;
    profileOccupation?: string;
    profileSecretaryLicenseNo?: string;
    profileSecretaryCertificateNo?: string;
    profileOtherDirectorship?: string;
    profileFirmNo?: string;
    profileNewRegNo?: string;
    profileIssuingCountry?: string;
    profilePlaceOfBirth?: string;
    profileIsMeeting?: number | null;
    isDel?: boolean;
    sysregdate?: string;
    sysmoddate?: string;
}

export interface CompanyRelationRoleAddress {
    positionCd?: string;
    positionName?: string;
    addressType?: string;
    addressLabel?: string;
    addressLine: string;
}

/** SG Company Relations 엑셀 전용 — Officer 행의 직책 단위 상세 */
export interface CompanyRelationPositionDetail {
    name: string;
    positionCd?: string;
    isNominee?: boolean;
    cessationDate?: string;
}

export interface CompanyRelation {
    companyCd: string;
    companyName: string;
    /** SG 엑셀 전용 — 그 회사의 L_ENTITY.REG_NO */
    companyRegNo?: string;
    role: string;
    /** MY: Register 직책별 주소 한 줄 요약 */
    addressSummary?: string;
    /** MY: entity+직책별 주소 전체 (Register L_CONTACT_ADDRESS / L_CONTACT_BRANCH) */
    roleAddresses?: CompanyRelationRoleAddress[];
    /** @deprecated MY — 레거시 CONTACT_JSON_DATA 한 줄 */
    address?: string;
    /** MY service_address에서 온 행 구분용 (선택) */
    relationSource?: string;
    /**
     * Officer인 경우 POSITION_NAME (예: Director, CEO 등)
     */
    position?: string;
    /**
     * SG 엑셀 전용 — Officer 행의 직책 상세.
     * position 은 회사 단위로 합쳐지고 cessationDate 도 "가장 늦은 해임일" 하나라
     * 직책별 nominee/해임 판정은 이 배열로 한다.
     */
    positionsDetail?: CompanyRelationPositionDetail[];
    shares?: number;
    currency?: string;
}

export interface ContactDeleteBlocker {
    entityCd: string;
    entityName: string;
    role: string;
    /** Officer인 경우 POSITION_NAME (예: Director, CEO) */
    position?: string;
}

export interface ContactDeleteBlockersResponse {
    contactCd: string;
    canDelete: boolean;
    blockers: ContactDeleteBlocker[];
}

export interface KYCDocumentStatus {
    visaExpiryDate?: string;
    nationalIdExpiryDate?: string;
    passportExpiryDate?: string;
}

export interface KYCHistory {
    no: number;
    requestDate: string;
    registrationRenewalDate: string;
}

export interface ContactListParams {
    page?: number;
    limit?: number;
    category?: string;
    contactType?: ContactRegisterType;
    search?: string;
    name?: string;
    email?: string;
    all?: boolean;
}

export interface ContactListResponse {
    contacts: Contact[];
    total: number;
    page: number;
    limit: number;
}

export interface UpdateContactData {
    identificationType?: string;
    /** 법인 ID Type (JSON: {"TypeOfIdentificationOfShareholder":"..."}) → L_GLOBAL_CONTACT.ID_TYPE */
    idTypeJson?: string;
    identificationNo?: string;
    nationality?: string;
    dateOfBirth?: string;
    passportNoExpiryDate?: string;
    firstName?: string;
    middleName?: string;
    surname?: string;
    entityName?: string;
    jurisdiction?: string;
    incorporationDate?: string;
    representativeName?: string;
    legalForm?: string;
    incorporationStatute?: string;
    corporateEntityRegister?: string;
    publicAccountingEntities?: 'Yes' | 'No'; // AUDITTING 컬럼
    email?: string;
    emails?: string[]; // 이메일 배열 (최대 2개)
    emailMainYn?: boolean[]; // 각 이메일의 MAIN_YN 값
    emailSyncYn?: Array<boolean | number>; // 각 이메일의 SYNC_YN 값
    mobileNo?: string;
    countryCode?: string;
    areaCode?: string;
    note?: string;
    profileGender?: string;
    profileRace?: string;
    profileOccupation?: string;
    profileSecretaryLicenseNo?: string;
    profileSecretaryCertificateNo?: string;
    profileOtherDirectorship?: string;
    profileFirmNo?: string;
    profileNewRegNo?: string;
    profileIssuingCountry?: string;
    profilePlaceOfBirth?: string;
    profileIsMeeting?: number | null;
    // Address fields
    addressTypeCd?: string;
    postalCode?: string;
    blockNo?: string;
    streetName?: string;
    buildingName?: string;
    addrLevel?: string;
    addrUnit?: string;
    foreignAddressLine1?: string;
    foreignAddressLine2?: string;
    altAddressTypeCd?: string;
    altPostalCode?: string;
    altBlockNo?: string;
    altStreetName?: string;
    altBuildingName?: string;
    altAddrLevel?: string;
    altAddrUnit?: string;
    altForeignAddressLine1?: string;
    altForeignAddressLine2?: string;
    localAddress?: AddressInfo;
    foreignAddress?: AddressInfo;
    altLocalAddress?: AddressInfo;
    altForeignAddress?: AddressInfo;
    IS_DEL?: boolean; // Soft delete flag
}

// Helper functions
export const isIndividual = (categoryCd: string) => categoryCd === CategoryCode.Individual;
export const isCorporate = (categoryCd: string) => categoryCd === CategoryCode.Corporate;

// File Upload Types
export interface FileUploadData {
    columns: string[];
    rows: any[];
}

export interface ColumnMapping {
    [systemColumn: string]: string | null; // systemColumn -> fileColumn
}

export interface ValidatedRow {
    index: number;
    data: Record<string, any>;
    isDuplicate: boolean;
    duplicateReason?: string;
    isMissingRequired: boolean;
    missingRequiredReason?: string;
}

export interface FileUploadResponse {
    total: number;
    success: number;
    failed: number;
    results: Array<{
        index: number;
        success: boolean;
        contactCd?: string;
        error?: string;
    }>;
}

export interface UploadContactData {
    contactType: 'Individual' | 'Corporate';
    data: Array<CreateIndividualData | CreateCorporateData>;
}

// System Column Definitions for File Upload
export interface SystemColumn {
    key: string;
    label: string;
    required: boolean;
}

// Individual 시스템 컬럼 목록 (Basic 7개 + Contact 5개 = 12개)
// 필수 필드: Identification No, First Name, Last Name, Email
export const INDIVIDUAL_SYSTEM_COLUMNS: SystemColumn[] = [
    { key: 'ID_TYPE_CD', label: 'Identification Type', required: false },
    { key: 'CONTACT_REG_NO', label: 'Identification No.', required: true },
    { key: 'NATIONALITY_CD', label: 'Nationality/Citizenship', required: false },
    { key: 'DATE_OF_BIRTH', label: 'Date of Birth', required: false },
    { key: 'FIRST_NAME', label: 'First Name', required: true },
    { key: 'MIDDLE_NAME', label: 'Middle Name', required: false },
    { key: 'SURNAME', label: 'Last Name', required: true },
    { key: 'COUNTRY_CD', label: 'Country Code', required: false },
    { key: 'AREA_CODE', label: 'Area Code', required: false },
    { key: 'MOBILE_NO', label: 'Mobile No.', required: false },
    { key: 'CONTACT_EMAIL', label: 'Email Address', required: true },
    { key: 'CONTACT_EMAIL_2', label: 'Email Address 2', required: false },
    { key: 'NOTE', label: 'Note', required: false },
];

// Corporate 시스템 컬럼 목록 (Basic 5개 + Contact 5개 = 10개)
// 필수 필드: Entity Name, Registration Number
// Representative Name은 File Upload에서 제외
export const CORPORATE_SYSTEM_COLUMNS: SystemColumn[] = [
    { key: 'JURISDICTION', label: 'Jurisdiction', required: false },
    { key: 'AUDITOR_YN', label: 'Public Accounting Entities', required: false },
    { key: 'CONTACT_NAME', label: 'Entity Name', required: true },
    { key: 'CONTACT_REG_NO', label: 'Registration Number', required: true },
    { key: 'DATE_OF_INCORPORATION', label: 'Date of Incorporation', required: false },
    { key: 'COUNTRY_CD', label: 'Country Code', required: false },
    { key: 'AREA_CODE', label: 'Area Code', required: false },
    { key: 'MOBILE_NO', label: 'Mobile No.', required: false },
    { key: 'CONTACT_EMAIL', label: 'Email Address', required: false },
    { key: 'CONTACT_EMAIL_2', label: 'Email Address 2', required: false },
    { key: 'NOTE', label: 'Note', required: false },
];

